import { useEffect, useLayoutEffect } from "react";
import { useAuth } from "@workos-inc/authkit-react";
import { setTokenAccessor, setSessionEnder } from "@/shared/api/clients/tokenAccessor";
import { useSetOrgId } from "@/shared/auth/orgContext";

function decodeJwtPayload(token: string): Record<string, unknown> {
  const [, payload] = token.split(".");
  return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
}

export function AuthTokenBridge() {
  const { getAccessToken, signOut, user, organizationId } = useAuth();
  const userId = user?.id;
  const setOrganizationId = useSetOrgId();

  useLayoutEffect(() => {
    setTokenAccessor(getAccessToken);
    setSessionEnder(() => {
      signOut({ returnTo: `${window.location.origin}/login` });
    });
  }, [getAccessToken, signOut]);

  useEffect(() => {
    // A previous session's token can resolve after logout or an organization
    // change. Clear the old scope immediately and ignore those late results.
    let cancelled = false;
    setOrganizationId(null);
    if (!userId) return;

    const resolveOrganization = async () => {
      try {
        const payload = decodeJwtPayload(await getAccessToken());
        if (!cancelled) {
          setOrganizationId(
            typeof payload.org_id === "string" && payload.org_id.length > 0
              ? payload.org_id
              : null,
          );
        }
      } catch {
        // Token acquisition/decoding can fail during session recovery. Keep
        // station-scoped queries disabled; the auth layer owns recovery.
        if (!cancelled) setOrganizationId(null);
      }
    };
    void resolveOrganization();
    return () => { cancelled = true; };
  }, [userId, organizationId, getAccessToken, setOrganizationId]);

  return null;
}
